import { useEffect, useRef, useState } from "react";
import { AxiosResponse } from "axios";
import liff from "@line/liff";
import api, { handleRequest } from "@services/axios/apiManager";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useUser, IdentityContext } from "@utils/store/user/useUser";
import { getSendOtpErrorMessage } from "@utils/otpError";
import { isUnauthorized } from "@utils/lineSession";
import { maskPhone } from "@utils/func/maskPhone";
import { clearFamilyInviteCode } from "@utils/familyInvite";
import { track } from "@lib/tracker";
import Screen from "./joinRegister/Screen";
import { OtpContent } from "./joinRegister/Otp";
import { PrimaryBtn } from "./joinRegister/Buttons";

/**
 * 未註冊者在 /account/addAccount 頁內完成「註冊＋加入家庭」：
 * 邀請碼預檢（不消費）→ 手機號碼（複製自 onboarding NpPhone）→ OTP（複製自 NpOtp）
 * → /line/register-by-invite 靜默註冊＋加入。整段停留在 addAccount，不導去 /register 頁。
 */

const OTP_RESEND_SECS = 60;

/** 查 OTP 重送冷卻剩餘秒數（不發送、不計費）。複製自 onboarding registerApi.fetchOtpCooldown。 */
const fetchOtpCooldown = (accessToken: string, phone: string) =>
  api.post<{ status?: string; data: { resendAvailableInSeconds: number } }>(
    "/app-api/line/phone-otp-cooldown",
    { accessToken, phone },
    { isAuth: false },
  );

/** 從 axios error 取出後端訊息。 */
const extractErrorMessage = (error: unknown): string => {
  if (error && typeof error === "object" && "response" in error) {
    const res = (error as { response?: { data?: { message?: unknown; error?: unknown } } }).response;
    const data = res?.data;
    if (data) {
      if (typeof data.message === "string") return data.message;
      if (Array.isArray(data.message)) return data.message.join(", ");
      if (typeof data.error === "string") return data.error;
    }
  }
  if (error instanceof Error) return error.message;
  return "";
};

interface JoinRegisterProps {
  code: string;
  onBack: () => void;
  onSuccess: (info: Record<string, unknown>) => void;
  onError: (type: string, code: string) => void;
}

export default function JoinRegister({ code, onBack, onSuccess, onError }: JoinRegisterProps) {
  const { lineAccessToken } = useLineAuth();
  const { setCusJwtToken } = useCusAuth();
  const { setChooseIdentity } = useUser();

  const [step, setStep] = useState<"checking" | "phone" | "otp">("checking");

  // ===== 邀請碼預檢（不消費）：無效就直接進 join_error，不讓使用者白走手機驗證 =====
  useEffect(() => {
    if (!code || !lineAccessToken) return;
    let active = true;
    api
      .post("/app-api/line/invite-code-check", { accessToken: lineAccessToken, invite_code: code }, { isAuth: false })
      .then((res) => {
        if (!active) return;
        const { valid, reason } = (res?.data?.data ?? {}) as { valid?: boolean; reason?: string | null };
        if (valid) {
          setStep("phone");
        } else {
          onError((reason ?? "").toLowerCase().includes("expired") ? "expired" : "invalid", code);
        }
      })
      .catch(() => {
        if (!active) return;
        onError("invalid", code);
      });
    return () => {
      active = false;
    };
  }, [code, lineAccessToken]);

  // ===== 手機頁狀態（複製自 NpPhone） =====
  const [ph, setPh] = useState("09");
  const digits = ph.replace(/\D/g, "");
  const valid = /^09\d{8}$/.test(digits);
  const [phone, setPhone] = useState("");

  // ===== OTP 頁狀態（複製自 NpOtp） =====
  const [otp, setOtp] = useState(["", "", "", "", ""]);
  const [sent, setSent] = useState(false);
  const [resendUntil, setResendUntil] = useState<number | undefined>(undefined);
  const [done, setDone] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [otpResetSignal, setOtpResetSignal] = useState(0);
  const full = otp.join("").length === 5;
  const sentPhone = useRef<string | null>(null);
  const triedCode = useRef<string | null>(null);
  const registering = useRef(false);

  const sendOtp = async () => {
    if (!phone) return;
    if (!lineAccessToken) {
      liff.login();
      return;
    }
    setError("");
    try {
      const res = await api.post("/app-api/line/send-phone-otp", { accessToken: lineAccessToken, phone }, { isAuth: false });
      setSent(true);
      const secs = Number(res?.data?.data?.resendAvailableInSeconds) || OTP_RESEND_SECS;
      setResendUntil(Math.floor(Date.now() / 1000) + secs);
    } catch (e) {
      // 429 會帶剩餘秒數 → 讓倒數接上後端真正的冷卻，而不是從 60 重數
      const left = Number(
        (e as { response?: { data?: { resendAvailableInSeconds?: number } } })?.response?.data
          ?.resendAvailableInSeconds,
      );
      if (Number.isFinite(left) && left > 0) {
        setResendUntil(Math.floor(Date.now() / 1000) + left);
        setSent(true); // 冷卻中代表先前確實寄出過，畫面應顯示倒數而非錯誤
      }
      if (isUnauthorized(e)) return;
      setError(getSendOtpErrorMessage(e));
    }
  };

  // 進入 OTP 步驟（或門號變動）時送出 OTP。
  useEffect(() => {
    if (step !== "otp" || !phone || sentPhone.current === phone) return;
    sentPhone.current = phone;
    track("funnel_step", { funnel: "join", step: "otp_send", entry: "addAccount" });
    void sendOtp();
  }, [step, phone]);

  // 進入 OTP 步驟時向後端查目前冷卻，接續倒數（重整／返回再進來都不會歸零）。
  useEffect(() => {
    if (step !== "otp" || !phone || !lineAccessToken) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetchOtpCooldown(lineAccessToken, phone);
        const secs = Number(res?.data?.data?.resendAvailableInSeconds);
        if (!cancelled && Number.isFinite(secs) && secs > 0) {
          setResendUntil(Math.floor(Date.now() / 1000) + secs);
          setSent(true);
        }
      } catch {
        // 忽略：查冷卻失敗不該擋住驗證流程
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, phone, lineAccessToken]);

  // 填滿 5 碼 → 向後端驗證；同一組碼驗證失敗過不重打，需使用者改動後才重試
  useEffect(() => {
    if (step !== "otp" || !full || done || verifying) return;
    const otpValue = otp.join("");
    if (triedCode.current === otpValue) return;
    if (!lineAccessToken) {
      liff.login();
      return;
    }
    triedCode.current = otpValue;
    setVerifying(true);
    setError("");
    track("funnel_step", { funnel: "join", step: "otp_submit", entry: "addAccount" });
    handleRequest({
      asyncRequest: () => api.post("/app-api/line/verify-phone-otp", { accessToken: lineAccessToken, phone, otpCode: otpValue }, { isAuth: false }),
      successCallback: (res: AxiosResponse) => {
        const phoneVerifiedTicket = res?.data?.data?.phoneVerifiedTicket || "";
        registerByInvite(phoneVerifiedTicket);
      },
      errorCallback: () => {
        setError("驗證碼錯誤，請重新輸入");
        setOtp(["", "", "", "", ""]);
        setOtpResetSignal((n) => n + 1); // 清空後游標回第一格，使用者可直接重打
        setVerifying(false);
      },
    });
  }, [step, full, otp, done, verifying, phone, lineAccessToken]);

  // OTP 驗證成功 → 靜默註冊＋加入家庭
  const registerByInvite = (phoneVerifiedTicket: string) => {
    if (registering.current) return;
    registering.current = true;
    track("funnel_step", { funnel: "join", step: "register_submit", entry: "addAccount" });
    handleRequest({
      asyncRequest: () =>
        api.post(
          "/app-api/line/register-by-invite",
          { accessToken: lineAccessToken, phoneVerifiedTicket, invite_code: code },
          { isAuth: false },
        ),
      successCallback: async (res: AxiosResponse) => {
        const data = res?.data?.data ?? {};
        if (data.jwtToken) {
          sessionStorage.setItem("jwtToken", data.jwtToken);
          setCusJwtToken(data.jwtToken);
        }
        clearFamilyInviteCode();
        const info = await enrichInfo(data.jwtToken || "", data.context_id);
        setDone(true);
        setVerifying(false);
        // 顯示成功動畫短暫停留 → 進 join_success
        setTimeout(() => onSuccess(info), 600);
      },
      errorCallback: (err: unknown) => {
        registering.current = false;
        setVerifying(false);
        const msg = extractErrorMessage(err);
        onError(msg.toLowerCase().includes("expired") ? "expired" : "invalid", code);
      },
    });
  };

  /** 註冊完成後撈 contexts 清單補帳戶名稱/瓦斯行，給 JoinSuccess 顯示。 */
  const enrichInfo = async (jwtToken: string, contextId?: number): Promise<Record<string, unknown>> => {
    const minimal = {
      name: "瓦斯帳戶",
      vendor: "瓦斯行",
      role: "成員",
      status: "active",
      context_id: contextId,
    };
    if (!jwtToken || !contextId) return minimal;
    try {
      const res = await api.get("/app-api/customer/contexts", {
        headers: { Authorization: `Bearer ${jwtToken}` },
      });
      // contexts API 回 { contexts, default_context_id }（見 IdentitySwitchMenu）；
      // 防禦性兼容直接回陣列的舊形狀。
      const data = res.data?.data;
      const list = Array.isArray(data) ? data : (data?.contexts ?? []);
      const contexts = list as IdentityContext[];
      const joined = contexts.find((c) => c.context_id === contextId);
      if (joined) {
        // 預抓身分：直接把剛加入的家庭設為目前身分，「完成 → 回首頁」時 /home
        // 立刻能帶 X-Context 抓資料，不用等 IdentitySwitchMenu 再跑一輪 contexts。
        setChooseIdentity(joined);
        return {
          ...minimal,
          name: joined.context_name,
          vendor: joined.primary_supplier?.supplier_name ?? "瓦斯行",
          role: joined.role === "owner" ? "負責人" : "成員",
        };
      }
    } catch {
      // 撈不到清單就用最小資訊呈現
    }
    return minimal;
  };

  // ===== 畫面 =====
  if (step === "checking") {
    return (
      <Screen title="加入既有的瓦斯帳戶" subtitle="確認邀請碼中…" onBack={onBack}>
        <div className="flex flex-col items-center justify-center py-10 gap-3">
          <div className="w-[22px] h-[22px] rounded-full border-[3px] border-[#2196f3] border-t-transparent animate-spin" />
          <div className="text-[14px] text-[#64748B]">確認邀請碼中…</div>
        </div>
      </Screen>
    );
  }

  if (step === "phone") {
    return (
      <Screen
        title="加入既有的瓦斯帳戶"
        subtitle={`邀請碼 ${code} 有效，驗證手機後即完成加入，免填其他資料。`}
        onBack={onBack}
        footer={
          <PrimaryBtn
            active={valid}
            onClick={() => {
              setPhone(digits);
              setStep("otp");
            }}>
            發送驗證碼
          </PrimaryBtn>
        }>
        <div className="text-[13px] text-[#475569] font-semibold mb-2">手機號碼</div>
        <div className="flex gap-2.5">
          <div className="flex items-center px-4 h-[54px] rounded-[14px] bg-[#F1F5F9] font-bold text-base text-[#333] shrink-0">+886</div>
          <input
            value={ph}
            onChange={(e) => setPh(e.target.value)}
            inputMode="numeric"
            placeholder="0912 345 678"
            className="flex-1 h-[54px] px-4 border-none rounded-[14px] bg-[#F1F5F9] text-[17px] outline-none box-border text-[#333]"
          />
        </div>
        {digits.length > 10 && (
          <div className="mt-2 text-[12px] text-[#EF4444] font-semibold">手機號碼為 10 碼，請確認是否多打了。</div>
        )}
        <div className="mt-2.5 text-[11.5px] text-[#64748B] leading-[1.6]">
          我們會傳送驗證碼確認本人。門號僅用於身分確認，不會對外顯示。
        </div>
      </Screen>
    );
  }

  return (
    <Screen
      title="輸入驗證碼"
      subtitle={
        done ? "驗證成功"
        : sent ? `已將驗證碼傳送至 ${maskPhone(phone)}`
        : error ? "驗證碼發送失敗"
        : `正在將驗證碼傳送至 ${maskPhone(phone)}…`
      }
      onBack={() => setStep("phone")}
    >
      {done ? (
        <div className="flex flex-col items-center justify-center py-10 gap-3">
          <div className="w-16 h-16 rounded-full bg-[#10B981] flex items-center justify-center jr-rise">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
              <path d="M5 12l5 5L20 7" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="text-[16px] font-extrabold text-[#15803D]">驗證成功，正在加入家庭…</div>
        </div>
      ) : (
        <>
          <OtpContent otp={otp} setOtp={setOtp} onResend={sendOtp} disabled={verifying} resetSignal={otpResetSignal} sent={sent} resendUntil={resendUntil} />
          {error ? (
            <div className="mt-3 text-center text-[13px] font-semibold text-[#EF4444]">{error}</div>
          ) : (
            <div className="mt-5 text-center text-[13px] text-[#94A3B8]">{verifying ? "驗證中…" : "輸入完成後自動驗證"}</div>
          )}
        </>
      )}
    </Screen>
  );
}
